import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Clock, 
  Copy, 
  ExternalLink, 
  ArrowUpRight, 
  ArrowDownLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  RefreshCw
} from 'lucide-react';

interface Transaction {
  hash: string;
  blockNumber: number;
  from: string;
  to: string;
  value: string;
  gasUsed: number;
  gasPrice: number;
  timestamp: string;
  status: 'success' | 'failed' | 'pending';
  type: 'transfer' | 'energy' | 'staking' | 'bridge' | 'contract';
  method?: string;
}

const TransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const transactionsPerPage = 25;
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchTransactions();
    const interval = setInterval(fetchTransactions, 10000); // 10초마다 업데이트
    return () => clearInterval(interval);
  }, [currentPage, filterType, filterStatus]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // 실제 API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const apiUrl = `${apiBaseUrl}/explorer/transactions?limit=500`;
      
      console.log('🔍 트랜잭션 API 호출:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      let transactionsData: Transaction[] = [];
      
      if (!response.ok) {
        console.error('❌ API 응답 실패:', response.status, response.statusText);
        setTransactions([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      console.log('📦 API 응답 데이터:', data);
      
      if (data.success && data.transactions && Array.isArray(data.transactions)) {
        console.log(`✅ 트랜잭션 ${data.transactions.length}개 수신`);
        
        transactionsData = data.transactions.map((tx: any) => {
          // 트랜잭션 타입 감지
          let txType: 'transfer' | 'energy' | 'staking' | 'bridge' | 'contract' = 'transfer';
          if (!tx.to) {
            txType = 'contract';
          } else {
            const value = parseFloat(tx.value || '0');
            // VTTN 토큰 주소 체크 (에너지 관련)
            const vttnAddress = process.env.REACT_APP_VTTN_TOKEN_ADDRESS?.toLowerCase();
            if (vttnAddress && tx.to.toLowerCase() === vttnAddress) {
              txType = 'energy';
            } else if (value >= 1000) {
              // 큰 금액은 스테이킹 가능성
              txType = 'staking';
            } else if (value > 0) {
              txType = 'transfer';
            }
          }

          return {
            hash: tx.hash,
            blockNumber: tx.blockNumber || 0,
            from: tx.from || '0x0000000000000000000000000000000000000000',
            to: tx.to || null,
            value: tx.value || '0',
            gasUsed: parseInt(tx.gasUsed || '0'),
            gasPrice: parseInt(tx.gasPrice || '0'),
            timestamp: tx.timestamp ? (typeof tx.timestamp === 'number' ? new Date(tx.timestamp * 1000).toISOString() : new Date(tx.timestamp).toISOString()) : new Date().toISOString(),
            status: tx.status === 'success' ? 'success' : tx.status === 'failed' ? 'failed' : 'pending',
            type: txType,
            method: tx.method || null
          };
        });
      } else {
        console.warn('⚠️ API 응답 형식 오류:', data);
      }
      
      if (transactionsData.length === 0) {
        console.warn('⚠️ 트랜잭션 데이터가 없습니다. 블록체인에 트랜잭션이 없을 수 있습니다.');
      }
      
      // 필터링 적용
      let filteredTransactions = transactionsData;
      
      if (filterType !== 'all') {
        filteredTransactions = filteredTransactions.filter(tx => tx.type === filterType);
      }
      
      if (filterStatus !== 'all') {
        filteredTransactions = filteredTransactions.filter(tx => tx.status === filterStatus);
      }
      
      if (searchTerm) {
        filteredTransactions = filteredTransactions.filter(tx => 
          tx.hash.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (tx.to && tx.to.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      setTransactions(filteredTransactions);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setLoading(false);
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'transfer': return 'bg-blue-100 text-blue-800';
      case 'energy': return 'bg-green-100 text-green-800';
      case 'staking': return 'bg-purple-100 text-purple-800';
      case 'bridge': return 'bg-orange-100 text-orange-800';
      case 'contract': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const paginatedTransactions = transactions.slice(
    (currentPage - 1) * transactionsPerPage,
    currentPage * transactionsPerPage
  );

  const totalPages = Math.ceil(transactions.length / transactionsPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Transactions</h1>
          <p className="text-slate-600">Real-time transaction monitoring on Voltron Mainnet</p>
        </div>
        <button
          onClick={fetchTransactions}
          className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{transactions.length.toLocaleString()}</p>
            </div>
            <Zap className="w-8 h-8 text-orange-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {transactions.length > 0 
                  ? ((transactions.filter(tx => tx.status === 'success').length / transactions.length) * 100).toFixed(1)
                  : '0.0'
                }%
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Gas Price</p>
              <p className="text-2xl font-bold text-blue-600">
                {transactions.length > 0
                  ? (transactions.reduce((sum, tx) => sum + tx.gasPrice, 0) / transactions.length / 1e9).toFixed(1)
                  : '0.0'
                } Gwei
              </p>
            </div>
            <Zap className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Energy Txs</p>
              <p className="text-2xl font-bold text-green-600">
                {transactions.filter(tx => tx.type === 'energy').length}
              </p>
            </div>
            <ArrowUpRight className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by hash, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="transfer">Transfer</option>
            <option value="energy">Energy</option>
            <option value="staking">Staking</option>
            <option value="bridge">Bridge</option>
            <option value="contract">Contract</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* 트랜잭션 테이블 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Hash</th>
                <th>Block</th>
                <th>From</th>
                <th>To</th>
                <th>Value</th>
                <th>Type</th>
                <th>Status</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="flex justify-center items-center space-x-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                      <span className="text-gray-500">Loading transactions...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-500">
                    No transactions found
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map((tx) => (
                  <tr key={tx.hash} className="hover:bg-gray-50">
                    <td>
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Zap className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              setSelectedTransaction(tx);
                              setShowDetailModal(true);
                            }}
                            className="font-medium text-blue-600 hover:text-blue-800"
                          >
                            {tx.hash.slice(0, 10)}...{tx.hash.slice(-8)}
                          </button>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-sm text-gray-500 font-mono">
                              {tx.hash.substring(0, 10)}...{tx.hash.slice(-8)}
                            </span>
                            <button
                              onClick={() => copyToClipboard(tx.hash)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Link 
                        to={`/block/${tx.blockNumber}`}
                        className="font-medium text-blue-600 hover:text-blue-800"
                      >
                        #{tx.blockNumber.toLocaleString()}
                      </Link>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Link 
                          to={`/address/${tx.from}`}
                          className="font-mono text-sm text-gray-600 hover:text-blue-600"
                        >
                          {formatAddress(tx.from)}
                        </Link>
                        <button
                          onClick={() => copyToClipboard(tx.from)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <ArrowDownLeft className="w-4 h-4 text-gray-400" />
                        <Link 
                          to={`/address/${tx.to}`}
                          className="font-mono text-sm text-gray-600 hover:text-blue-600"
                        >
                          {formatAddress(tx.to)}
                        </Link>
                        <button
                          onClick={() => copyToClipboard(tx.to)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm font-medium text-green-600">
                        {parseFloat(tx.value).toFixed(4)} VTTN
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-1 rounded-lg text-sm font-medium ${getTypeColor(tx.type)}`}>
                        {tx.type}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(tx.status)}
                        <span className={`text-sm font-medium ${
                          tx.status === 'success' ? 'text-green-600' :
                          tx.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {tx.status}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {formatTimeAgo(tx.timestamp)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
            if (page > totalPages) return null;
            
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-2 border rounded-lg ${
                  currentPage === page
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            );
          })}
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* 트랜잭션 상세 모달 */}
      {showDetailModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Transaction Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Transaction Info</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hash:</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono">{selectedTransaction.hash.slice(0, 20)}...</span>
                        <button
                          onClick={() => navigator.clipboard.writeText(selectedTransaction.hash)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Block:</span>
                      <span className="font-medium">{selectedTransaction.blockNumber.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${
                        selectedTransaction.status === 'success' ? 'bg-green-100 text-green-800' :
                        selectedTransaction.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {selectedTransaction.status === 'success' && <CheckCircle className="w-3 h-3" />}
                        {selectedTransaction.status === 'failed' && <XCircle className="w-3 h-3" />}
                        {selectedTransaction.status === 'pending' && <Clock className="w-3 h-3" />}
                        <span className="capitalize">{selectedTransaction.status}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type:</span>
                      <span className="capitalize font-medium">{selectedTransaction.type}</span>
                    </div>
                    {selectedTransaction.method && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Method:</span>
                        <span className="font-mono text-sm">{selectedTransaction.method}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Value & Gas</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Value:</span>
                      <span className="font-medium">{parseFloat(selectedTransaction.value).toFixed(4)} VTTN</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gas Used:</span>
                      <span className="font-medium">{selectedTransaction.gasUsed.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gas Price:</span>
                      <span className="font-medium">{selectedTransaction.gasPrice.toFixed(2)} Gwei</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Transaction Fee:</span>
                      <span className="font-medium">{((selectedTransaction.gasUsed * selectedTransaction.gasPrice) / 1e9).toFixed(6)} VTTN</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Timestamp:</span>
                      <span className="font-medium">{new Date(selectedTransaction.timestamp).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 주소 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Addresses</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">From</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded border">
                      <ArrowUpRight className="w-4 h-4 text-red-500" />
                      <span className="font-mono text-sm">{selectedTransaction.from}</span>
                      <button
                        onClick={() => navigator.clipboard.writeText(selectedTransaction.from)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">To</label>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded border">
                      <ArrowDownLeft className="w-4 h-4 text-green-500" />
                      <span className="font-mono text-sm">{selectedTransaction.to}</span>
                      <button
                        onClick={() => navigator.clipboard.writeText(selectedTransaction.to)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => window.open(`/block/${selectedTransaction.blockNumber}`, '_blank')}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View Block</span>
                </button>
                <button
                  onClick={() => window.open(`/address/${selectedTransaction.from}`, '_blank')}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View From Address</span>
                </button>
                <button
                  onClick={() => window.open(`/address/${selectedTransaction.to}`, '_blank')}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>View To Address</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TransactionsPage;